import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../../config/api';
import { useLocation, useNavigate } from 'react-router-dom';
import useCart from '../../hooks/useCart';
import './CheckoutStyles.css';


const CashfreePayment = ({ totalAmount, order, onSuccess, onError, onBack, selectedPaymentMethod }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cashfreeKey, setCashfreeKey] = useState('');
  const [cashfreeMode, setCashfreeMode] = useState('sandbox');
  const [cashfreeLoaded, setCashfreeLoaded] = useState(false);
  const [orderCreationAttempted, setOrderCreationAttempted] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { clearCart } = useCart();

  const isUserDroppedPayment = (errorLike) => {
    const raw = `${errorLike?.code || ''} ${errorLike?.type || ''} ${errorLike?.message || ''}`.toLowerCase();
    return raw.includes('user_dropped') ||
      raw.includes('dropped') ||
      raw.includes('closed') ||
      raw.includes('cancelled') ||
      raw.includes('canceled') ||
      raw.includes('not completed');
  };

  const handlePaymentOutcomeFailure = async ({
    paymentState = 'failed',
    message,
    cashfreeStatus,
    rawError
  }) => {
    const finalMessage = message || (
      paymentState === 'incomplete'
        ? 'Payment not completed. Please proceed again.'
        : 'Payment failed. Please try again.'
    );

    setError(finalMessage);
    setLoading(false);

    if (order && order._id) {
      await markOrderAsPaymentFailed(order._id, finalMessage, paymentState, cashfreeStatus);
    }

    if (onError) {
      onError({
        paymentState,
        message: finalMessage,
        rawError
      });
    }
  };

  // Check for order_id in URL on component mount (for return from Cashfree)
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const orderId = queryParams.get('order_id');
    
    if (orderId) {
      console.log('Detected return from Cashfree with order ID:', orderId);
      verifyPayment(orderId);
    }
  }, [location]);

  // Load Cashfree script
  useEffect(() => {
    const loadCashfreeScript = () => {
      return new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
        script.onload = () => {
          console.log('Cashfree script loaded successfully');
          setCashfreeLoaded(true);
          resolve(true);
        };
        script.onerror = () => {
          console.error('Cashfree script failed to load');
          setError('Payment system failed to load. Please refresh the page.');
          resolve(false);
        };
        document.body.appendChild(script);
      });
    };

    if (window.Cashfree) {
      console.log('Cashfree already loaded');
      setCashfreeLoaded(true);
    } else {
      console.log('Loading Cashfree script...');
      loadCashfreeScript();
    }
  }, []);

  // Fetch Cashfree key
  useEffect(() => {
    const fetchCashfreeKey = async () => {
      try {
        const response = await axios.get(`${API_BASE}/v1/payment/cashfree-key`, {
          withCredentials: true
        });
        if (response.data.success) {
          setCashfreeKey(response.data.key);
          setCashfreeMode(response.data.mode || 'sandbox');
        } else {
          setError('Could not initialize payment system. Please try again later.');
        }
      } catch (error) {
        setError('Could not initialize payment system. Please try again later.');
      }
    };

    fetchCashfreeKey();
  }, []);

  const handlePayment = async () => {
    if (!window.Cashfree || !cashfreeKey) {
      setError('Payment system is not available. Please refresh the page and try again.');
      return;
    }
    if (!order || !order._id) {
      setError('Order not found. Please try again.');
      return;
    }
    setLoading(true);
    setError('');
    setOrderCreationAttempted(true);
    try {
      const baseUrl = window.location.origin;
      // First, create the Cashfree order (get the real Cashfree order ID)
      const orderResponse = await axios.post(
        `${API_BASE}/v1/payment/cashfree/create-order`,
        {
          amount: totalAmount,
          currency: 'INR',
          order_id: order._id, // Use real backend order ID
          customer_details: {
            customer_id: `customer_${order._id}`,
            customer_phone: order.shippingAddress?.phone || '9999999999',
            customer_email: order.shippingAddress?.email || 'customer@example.com',
            customer_name: order.shippingAddress?.fullName || 'Test Customer'
          },
          // order_meta will be set after we get the Cashfree order ID
        },
        {
          withCredentials: true
        }
      );
      if (!orderResponse.data.success) {
        throw new Error(orderResponse.data.message || 'Could not create payment session');
      }
      const cashfreeOrder = orderResponse.data.order;
      console.log('Cashfree order created successfully:', cashfreeOrder);
      // Now set the return URL using the real Cashfree order ID
      const cashfreeOrderId = cashfreeOrder.order_id;
      const returnUrl = `${baseUrl}/order-confirmation?order_id=${cashfreeOrderId}`;
      // Update the order on Cashfree with the correct return_url (optional, if needed)
      // If Cashfree API supports updating the order_meta/return_url, do it here. Otherwise, ensure it's set at creation time in backend.
      // Initialize Cashfree using the global variable
      const cashfree = new window.Cashfree({
        mode: cashfreeMode,
      });
      // Clear previous dropin (if any)
      const dropinContainer = document.getElementById('cashfree-dropin');
      if (dropinContainer) dropinContainer.innerHTML = '';
      // Set checkout options
      const checkoutOptions = {
        paymentSessionId: cashfreeOrder.payment_session_id,
        redirectTarget: "_self",
      };
      console.log('Initializing Cashfree checkout with options:', checkoutOptions);
      cashfree.checkout(checkoutOptions).then(function(result) {
        if (result.error) {
          console.error("Payment failed:", result.error);
          const paymentState = isUserDroppedPayment(result.error) ? 'incomplete' : 'failed';
          handlePaymentOutcomeFailure({
            paymentState,
            message: paymentState === 'incomplete'
              ? 'Payment not completed. Please proceed again.'
              : (result.error.message || 'Payment failed. Please try again.'),
            cashfreeStatus: result.error.code || result.error.type,
            rawError: result.error
          });
        }
        if (result.redirect) {
          console.log("Redirecting to payment page");
        }
      }).catch(function(error) {
        console.error("Payment error:", error);
        const paymentState = isUserDroppedPayment(error) ? 'incomplete' : 'failed';
        handlePaymentOutcomeFailure({
          paymentState,
          message: paymentState === 'incomplete'
            ? 'Payment not completed. Please proceed again.'
            : 'Payment failed. Please try again.',
          cashfreeStatus: error.code || error.type,
          rawError: error
        });
      });
    } catch (error) {
      console.error('Cashfree error:', error);
      setError(error.message || 'Payment initialization failed. Please try again.');
      setLoading(false);
    }
  };

  const verifyPayment = async (orderId) => {
    console.log('Verifying payment for order:', orderId);
    setLoading(true);
    
    try {
      const verifyResponse = await axios.post(
        `${API_BASE}/v1/payment/verify-payment`,
        { order_id: orderId },
        { withCredentials: true }
      );
  
      console.log('Verification response:', verifyResponse.data);
      
      if (verifyResponse.data.success) {
        console.log('Payment verified successfully!');
        // Store order in sessionStorage for access on confirmation page
        sessionStorage.setItem('lastOrder', JSON.stringify(verifyResponse.data.order));
        
        // Make sure to store the correct payment method
        const paymentMethod = {
          id: 'cashfree',
          name: 'Online Payment'
        };
        sessionStorage.setItem('lastPaymentMethod', JSON.stringify(paymentMethod));
        
        // Clear cart (same as COD) so items don't remain after successful online payment
        clearCart();
        
        if (onSuccess) {
          // Call onSuccess callback if provided
          onSuccess(verifyResponse.data.order, paymentMethod);
        } else {
          // Directly navigate to the confirmation page if onSuccess is not provided
          navigate('/order-confirmation', {
            state: {
              order: verifyResponse.data.order,
              paymentMethod: paymentMethod
            }
          });
        }
      } else {
        console.error('Payment verification failed:', verifyResponse.data.message);
        await handlePaymentOutcomeFailure({
          paymentState: verifyResponse.data.paymentState || 'failed',
          message: verifyResponse.data.message,
          cashfreeStatus: verifyResponse.data.cashfreeStatus,
          rawError: verifyResponse.data
        });
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      await handlePaymentOutcomeFailure({
        paymentState: 'failed',
        message: 'Error verifying payment. Please contact support.',
        rawError: error
      });
    }
  };

  const handleCashOnDelivery = () => {
    // Implement cash on delivery logic
    console.log('Processing cash on delivery payment');
    if (onSuccess) {
      onSuccess({
        id: 'cod',
        name: 'Cash on Delivery',
        details: {
          status: 'PENDING',
          method: 'COD'
        }
      });
    } else {
      // If onSuccess is not provided, navigate directly
      navigate('/order-confirmation', {
        state: {
          order: {
            _id: `COD_${Date.now()}`,
            paymentMethod: 'COD',
            isPaid: false,
            paidAt: null
          },
          paymentMethod: {
            id: 'cod',
            name: 'Cash on Delivery'
          }
        }
      });
    }
  };

  const markOrderAsPaymentFailed = async (orderId, failureReason, paymentState = 'failed', cashfreeStatus = 'FAILED') => {
    try {
      const response = await axios.put(
        `${API_BASE}/${orderId}/payment-failed`,
        {
          failure_reason: failureReason,
          statusType: paymentState,
          payment_status: cashfreeStatus,
          cashfree_order_id: order?.cashfreeOrderId || order?.paymentResult?.id || orderId
        },
        { withCredentials: true }
      );
      console.log('Order marked as payment failed:', response.data);
    } catch (error) {
      console.error('Failed to mark order as payment failed:', error);
    }
  };

  return (
    <div className="cashfree-payment-container">
      <h2>Payment Options</h2>
      
      {error && (
        <div className="payment-error">
          <p>{error}</p> 
          <button onClick={() => setError('')} className="dismiss-button">Dismiss</button>
        </div>
      )}
      
      {loading ? (
        <div className="payment-loading">
          <div className="payment-loader"></div>
          <p>Processing your payment...</p>
        </div>
      ) : (
        <div className="payment-methods">
          <div className="payment-method-info">
            <div className="payment-method-icon">💳</div>
            <div className="payment-method-details">
              <h3>Online Payment</h3>
              <p>Pay securely using Credit/Debit Card, UPI, Netbanking, or Wallets</p>
              <div className="payment-method-logos">
                <span className="payment-logo visa">Visa</span>
                <span className="payment-logo mastercard">Mastercard</span>
                <span className="payment-logo upi">UPI</span>
                <span className="payment-logo netbanking">Netbanking</span>
              </div>
            </div>
          </div>
          
          <div className="order-summary">
            <h3>Order Summary</h3>
            <div className="order-total">
              <span>Total Amount:</span>
              <span>₹{totalAmount ? totalAmount.toFixed(2) : '0.00'}</span>
            </div>
          </div>
          
          <div className="payment-actions">
            <button onClick={onBack} className="back-button">Back</button>
            {selectedPaymentMethod && selectedPaymentMethod.id === 'cashfree' && (
              <button 
                onClick={handlePayment} 
                className="pay-button"
                disabled={loading || !cashfreeLoaded}
              >
                Pay Now
              </button>
            )}
            {selectedPaymentMethod && selectedPaymentMethod.id === 'cod' && (
              <button 
                onClick={handleCashOnDelivery} 
                className="cod-button"
                disabled={loading}
              >
                Continue with Cash on Delivery
              </button>
            )}
          </div>
        </div>
      )}
      
      <div id="cashfree-dropin"></div>
    </div>
  );
};

export default CashfreePayment;
